import {
  TrackToggle,
  useLocalParticipant,
  useRemoteParticipants,
  useTracks,
} from "@livekit/components-react";
import classNames from "./Controls.module.css";
import { RoomEvent, Track } from "livekit-client";
import UserLanguageSelect from "../../common/UserLanguageSelect";
import Voice from "../../common/Voice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLeaveMeeting, useMeeting } from "../../../hooks/meeting";
import Overlay from "../../common/Overlay";
import { createPortal } from "react-dom";
import EmojiPopover from "./EmojiPopover";
import LayoutPopover from "./LayoutPopover";
import { useRoom } from "../../context/room/room";
import { useRecordingId } from "../../../hooks/recording";
import CameraToggle from "../../common/CameraToggle";
import MicToggle from "../../common/MicToggle";
import Button from "../../common/Button";
import ConfirmLeaveDialog from "../../common/dialogs/ConfirmLeaveDialog";
import { ImPhoneHangUp } from "react-icons/im";
import { IoMdAdd } from "react-icons/io";
import { BsChatLeft, BsRecord2 } from "react-icons/bs";
import { GoPeople } from "react-icons/go";
import { RadixRecordingDialog } from "../../common/dialogs/RecordingDialog";
import ShareDialog from "../../common/dialogs/ShareDialog";
import {
  MdOutlineClosedCaption,
  MdOutlineClosedCaptionDisabled,
} from "react-icons/md";
import OverflowPopover from "./OverflowPopover";
import { LuScreenShare, LuScreenShareOff } from "react-icons/lu";
import { useIsMobile } from "../../../hooks/layout";
import { IoLanguage } from "react-icons/io5";

export default function Controls(props: { numUnreadMessages: number }) {
  // When remote participant shares screen, disable local screen share
  const remoteParticipants = useRemoteParticipants();
  const remoteParticipantScreenSharing = useMemo(() => {
    return !!remoteParticipants.filter((p) => p.isScreenShareEnabled).length;
  }, [remoteParticipants]);
  const { localParticipant } = useLocalParticipant();
  useEffect(() => {
    if (remoteParticipantScreenSharing) {
      localParticipant.setScreenShareEnabled(false);
    }
  }, [remoteParticipantScreenSharing, localParticipant]);

  const [meeting, setMeeting] = useMeeting();

  const recordingId = useRecordingId();

  // TODO: Re-add functionality for disabled button
  const [disableRecordButton] = useState<boolean>(false);

  const overlayWindowRef = useRef<Window>();

  const { room, organization } = useRoom();

  const closeOverlay = useCallback(() => {
    if (!overlayWindowRef.current) {
      return;
    }
    overlayWindowRef.current.close();
    setMeeting((prevState) => ({
      ...prevState,
      overlayOpen: false,
    }));
  }, [setMeeting]);

  const openOverlay = useCallback(async () => {
    overlayWindowRef.current =
      await // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).documentPictureInPicture.requestWindow({
        width: 300,
        height: 600,
      });

    // Copy style sheets to overlay window
    [...document.styleSheets].forEach((styleSheet) => {
      try {
        const cssRules = [...styleSheet.cssRules]
          .map((rule) => rule.cssText)
          .join("");
        const style = document.createElement("style");
        style.textContent = cssRules;
        overlayWindowRef.current?.document.head.appendChild(style);
      } catch (e) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.type = styleSheet.type;
        link.media = styleSheet.media.toString();
        link.href = styleSheet.href!;
        overlayWindowRef.current?.document.head.appendChild(link);
      }

      overlayWindowRef.current?.addEventListener("pagehide", () =>
        closeOverlay()
      );

      setMeeting((prevState) => ({
        ...prevState,
        overlayOpen: true,
      }));
    });
  }, [closeOverlay, setMeeting]);

  const toggleOverlay = useCallback(
    () => (meeting.overlayOpen ? closeOverlay() : openOverlay()),
    [meeting.overlayOpen, openOverlay, closeOverlay]
  );

  useEffect(() => {
    // @ts-expect-error No types
    if (!window.documentPictureInPicture) {
      return;
    }
    try {
      navigator.mediaSession.setActionHandler(
        // @ts-expect-error No types
        "enterpictureinpicture",
        openOverlay
      );
    } catch (error) {
      console.error(error);
    }
  }, [openOverlay]);

  const screenShareTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false }
  );

  const isMobile = useIsMobile();

  const [showLanguageSelect, setShowLangaugeSelect] = useState(false);

  const leaveMeeting = useLeaveMeeting();

  if (!room) {
    return;
  }

  return (
    <>
      {/* Overlay window renderer */}
      {overlayWindowRef.current &&
        createPortal(
          <div
            style={{
              display: meeting.overlayOpen ? "block" : "none",
              width: "100%",
              height: "100%",
            }}
          >
            <Overlay />
          </div>,
          overlayWindowRef.current.document.body
        )}
      <div className={classNames.Controls}>
        {showLanguageSelect && (
          <div className={classNames.LanguageSelectContainer}>
            <UserLanguageSelect
              languages={[
                ...(organization?.autodetect_enabled ? ["autodetect"] : []),
                ...(room?.languages || []),
              ]}
            />
          </div>
        )}
        {!isMobile && (
          <div>
            <LayoutPopover />

            <UserLanguageSelect
              languages={[
                ...(organization?.autodetect_enabled ? ["autodetect"] : []),
                ...(room?.languages || []),
              ]}
            />
          </div>
        )}
        <div>
          {isMobile && (
            <Button
              className={classNames.ControlsButton}
              color="gray"
              borderColor={showLanguageSelect ? "primary" : "dark-gray"}
              backgroundColor="black"
              onClick={() => setShowLangaugeSelect((prev) => !prev)}
            >
              <IoLanguage style={{ minWidth: "20px", minHeight: "20px" }} />
            </Button>
          )}

          <MicToggle className={classNames.TrackToggle} />
          <CameraToggle className={classNames.TrackToggle} />

          {organization?.voice_translation_enabled && (
            <Voice className={classNames.ControlsButton} hideText={true} />
          )}

          {!isMobile && (
            <TrackToggle
              className={`${classNames.TrackToggle} ${classNames.ScreenShareToggle}`}
              source={Track.Source.ScreenShare}
              captureOptions={{ audio: true, selfBrowserSurface: "include" }}
              showIcon={false}
            >
              {screenShareTracks.length &&
              screenShareTracks[0].participant.isLocal ? (
                <LuScreenShareOff />
              ) : (
                <LuScreenShare />
              )}
            </TrackToggle>
          )}

          {organization?.recording_enabled && !isMobile && (
            <RadixRecordingDialog
              roomId={room.id}
              start={!recordingId}
              trigger={
                <Button
                  disabled={disableRecordButton}
                  backgroundColor="black"
                  color={recordingId ? "red" : "gray"}
                  borderColor={recordingId ? "red" : "dark-gray"}
                  className={classNames.ControlsButton}
                >
                  <BsRecord2 style={{ minWidth: "24px", minHeight: "24px" }} />
                </Button>
              }
            />
          )}

          {!isMobile && (
            <Button
              className={classNames.ControlsButton}
              color="gray"
              borderColor={"dark-gray"}
              backgroundColor="black"
              onClick={() =>
                setMeeting((prevState) => ({
                  ...prevState,
                  layout: {
                    ...prevState.layout,
                    panels: {
                      ...prevState.layout.panels,
                      captions: !prevState.layout.panels.captions,
                    },
                  },
                }))
              }
            >
              {meeting.layout.panels.captions ? (
                <MdOutlineClosedCaption
                  style={{ minWidth: "20px", minHeight: "20px" }}
                />
              ) : (
                <MdOutlineClosedCaptionDisabled
                  style={{ minWidth: "20px", minHeight: "20px" }}
                />
              )}
            </Button>
          )}
          {!isMobile && <EmojiPopover />}
          <OverflowPopover
            toggleOverlay={toggleOverlay}
            className={classNames.ControlsButton}
            room={room}
          />

          {isMobile && (
            <ConfirmLeaveDialog
              link={`/meeting/${room.id}/survey`}
              onConfirm={leaveMeeting}
              trigger={
                <Button
                  color="gray"
                  borderColor="dark-gray"
                  backgroundColor="black"
                  className={classNames.ControlsButton}
                >
                  <ImPhoneHangUp style={{ color: "var(--langfinity-red)" }} />
                </Button>
              }
            />
          )}
        </div>
        {!isMobile && (
          <div>
            <Button
              className={classNames.ControlsButton}
              onClick={() =>
                setMeeting((prevState) => ({
                  ...prevState,
                  layout: {
                    ...prevState.layout,
                    panels: {
                      ...prevState.layout.panels,
                      side:
                        prevState.layout.panels.side !== "chat"
                          ? "chat"
                          : undefined,
                    },
                  },
                }))
              }
              color={meeting.layout.panels.side === "chat" ? "white" : "gray"}
              borderColor={
                meeting.layout.panels.side === "chat" ? "primary" : "dark-gray"
              }
              backgroundColor="black"
              style={{
                position: "relative",
              }}
            >
              <BsChatLeft strokeWidth={1.2} />
              {props.numUnreadMessages > 0 && (
                <div className={classNames.UnreadMessages}>
                  {props.numUnreadMessages}
                </div>
              )}
            </Button>
            <Button
              className={classNames.ControlsButton}
              onClick={() =>
                setMeeting((prevState) => ({
                  ...prevState,
                  layout: {
                    ...prevState.layout,
                    panels: {
                      ...prevState.layout.panels,
                      side:
                        prevState.layout.panels.side !== "participants"
                          ? "participants"
                          : undefined,
                    },
                  },
                }))
              }
              color={
                meeting.layout.panels.side === "participants" ? "white" : "gray"
              }
              borderColor={
                meeting.layout.panels.side === "participants"
                  ? "primary"
                  : "dark-gray"
              }
              backgroundColor="black"
            >
              <GoPeople strokeWidth={1.2} />
            </Button>
            <ShareDialog
              trigger={
                <Button
                  color="gray"
                  backgroundColor="black"
                  borderColor="dark-gray"
                  className={classNames.ControlsButton}
                >
                  <IoMdAdd style={{ minWidth: "20px", minHeight: "20px" }} />
                </Button>
              }
              shareUrl={window.location.href}
              description={"Copy the link to share with participants"}
            />

            <ConfirmLeaveDialog
              link={`/meeting/${room.id}/survey`}
              onConfirm={leaveMeeting}
              trigger={
                <Button
                  color="gray"
                  borderColor="dark-gray"
                  backgroundColor="black"
                  className={classNames.ControlsButton}
                >
                  <ImPhoneHangUp style={{ color: "var(--langfinity-red)" }} />
                </Button>
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
